import { useApolloClient } from "@apollo/client";
import {
  CardContainer,
  LabeledValue,
  LabeledValueWithButton,
  ModalOpenButton,
} from "@msys/ui";
import {
  Add as AddIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import {
  Button,
  ButtonProps,
  Checkbox,
  FormLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import React from "react";
import { useOutletContext } from "react-router-dom";
import { DocType, PermissionName } from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import { type DocumentByType } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { DecisionOptionRadioOrCheckbox } from "../fields/DecisionOptionRadioOrCheckbox.js";
import { useDecisionItemMutations } from "../hooks/useDecisionItemMutations.js";
import { AttributeExpressionModal } from "../modals/AttributeExpressionModal.js";
import {
  QuestionControlSection,
  QuestionControlSectionFormValues,
} from "../QuestionControlSection.js";
import {
  RulesBox_ItemFragment,
  RulesBox_QuestionControlForm_ItemFragment,
} from "./RulesBox.generated.js";
import { useRulesBox_QuestionControlForm_ModifyWizardSettingsMutationWithWizardRefetch } from "./RulesBox.mutations.js";

export function RulesBox({
  projectId,
  docId,
  item,
  canEdit,
  docType,
  documentViewerPermissions,
  refetchWizardQuestions,
}: {
  projectId: string | null;
  docId: string;
  item: RulesBox_ItemFragment;
  canEdit: boolean;
  docType: DocType;
  documentViewerPermissions: PermissionName[];
  refetchWizardQuestions: () => Promise<unknown>;
}) {
  const { t } = useTranslate(["QuoteItem"]);
  const { expandedItemIds } = useOutletContext<{
    expandedItemIds: string[] | undefined;
  }>();

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const {
    modifyItemContingency,
    modifyItemOptionality,
    handleEliminateOption,
    handleResetEliminateOption,
    eliminateItem,
    resetEliminateItem,
    loading,
  } = useDecisionItemMutations(
    projectId,
    docId,
    false,
    expandedItemIds,
    refetchWizardQuestions
  );

  const onChangeDecisionIs = async (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (loading) return;

    const decisionIsContingentItem = checked;
    const decisionContingentItemPreselection =
      checked === false ? null : undefined;

    await modifyItemOptionality({
      variables: {
        input: {
          projectId,
          docId,
          itemId: item.id,
          values: {
            decisionIsContingentItem,
            decisionContingentItemPreselection,
          },
        },
      },
    });
  };

  const handleItemEliminationToggle = async () => {
    if (loading) return;

    if (item.isItemEliminated) {
      await resetEliminateItem(item.id);
    } else {
      await eliminateItem(item.id);
    }
  };

  const onChangeDecisionPreselection = async () => {
    if (loading) return;

    await modifyItemContingency({
      variables: {
        input: {
          projectId,
          docId,
          itemId: item.id,
          values: {
            decisionContingentItemPreselection:
              !item.decisionContingentItemPreselection,
          },
        },
      },
    });
  };

  const handleDecisionOptionEliminationToggle = async () => {
    if (loading) return;

    if (item.decisionOptionElimination) {
      await handleResetEliminateOption(item.id);
    } else {
      await handleEliminateOption(item.id);
    }
  };

  if (!("attributeExpressions" in item)) return null;

  const eliminateItemIfExpression = item.attributeExpressions.find(
    expression => expression.attribute === "eliminateItemIf"
  );
  const decisionIsContingentItemExpression = item.attributeExpressions.find(
    expression => expression.attribute === "decisionIsContingentItem"
  );
  const decisionContingentItemPreselectionExpression =
    item.attributeExpressions.find(
      expression =>
        expression.attribute === "decisionContingentItemPreselection"
    );
  const preselectDecisionOptionIfExpression = item.attributeExpressions.find(
    expression => expression.attribute === "preselectDecisionOptionIf"
  );

  const eliminateDecisionOptionIfExpression = item.attributeExpressions.find(
    expression => expression.attribute === "eliminateDecisionOptionIf"
  );

  const itemIsDecisionOption = item.isDecisionOption;

  if (!canEdit) {
    // flag showing whether item has at least any info set in it
    const hasInfo =
      (item.isDecisionOption &&
        (Boolean(item.decisionOptionElimination) ||
          Boolean(
            item.attributeExpressions.find(
              expression => expression.attribute === "eliminateDecisionOptionIf"
            )
          ) ||
          Boolean(
            item.attributeExpressions.find(
              expression => expression.attribute === "preselectDecisionOptionIf"
            )
          ))) ||
      item.isItemEliminated ||
      Boolean(
        item.attributeExpressions.find(
          expression => expression.attribute === "eliminateItemIf"
        )
      ) ||
      item.decisionIsContingentItem ||
      Boolean(
        item.attributeExpressions.find(
          expression => expression.attribute === "decisionIsContingentItem"
        )
      );

    if (!hasInfo) {
      return null;
    }
  }

  return (
    <CardContainer
      isExpandable={true}
      isInitiallyClosed={false}
      title={t("Rules", { ns: "QuoteItem" })}
    >
      <Stack
        spacing={1}
        sx={{
          p: 1,
          pt: 0,
        }}
      >
        {itemIsDecisionOption ? (
          <>
            <Stack
              sx={{
                alignItems: "flex-start",
              }}
            >
              <Stack
                direction={"row"}
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignSelf: "stretch",
                }}
              >
                <FormLabel sx={{ color: "text.primary" }}>
                  {t("Decision option eliminated", {
                    ns: "QuoteItem",
                  })}
                </FormLabel>
                <Checkbox
                  id={"decisionElimination"}
                  disabled={!canEdit || loading}
                  checked={!!item.decisionOptionElimination}
                  onChange={handleDecisionOptionEliminationToggle}
                  size="small"
                />
              </Stack>
              {canEdit && (
                <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                  capability="expressions"
                  permissionByType={{
                    QUOTE: "quote_expressions_update",
                    REQUIREMENT: "requirement_expressions_update",
                    TEMPLATE: "template_expressions_update",
                    BUILDING: "building_expressions_update",
                  }}
                  document={document}
                  otherwise={
                    eliminateDecisionOptionIfExpression?.expr ? (
                      <LabeledValue
                        label={t("Condition", { ns: "QuoteItem" })}
                        labelIcon={FunctionsIcon}
                      >
                        {eliminateDecisionOptionIfExpression.expr}
                      </LabeledValue>
                    ) : undefined
                  }
                >
                  <ModalOpenButton
                    Modal={AttributeExpressionModal}
                    modalProps={{
                      projectId,
                      docId: docId,
                      itemId: item.id,
                      fieldName: "eliminateDecisionOptionIf",
                      expression: eliminateDecisionOptionIfExpression?.expr,
                    }}
                  >
                    {eliminateDecisionOptionIfExpression ? (
                      <LabeledValueWithButton
                        label={t("Condition", { ns: "QuoteItem" })}
                        labelIcon={FunctionsIcon}
                      >
                        {eliminateDecisionOptionIfExpression.expr}
                      </LabeledValueWithButton>
                    ) : (
                      <AddConditionButton />
                    )}
                  </ModalOpenButton>
                </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
              )}
            </Stack>
            {(item.decisionOptionElimination === undefined ||
              item.decisionOptionElimination === null) && (
              <Stack
                sx={{
                  alignItems: "flex-start",
                }}
              >
                <Stack
                  direction={"row"}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    alignSelf: "stretch",
                  }}
                >
                  <Typography>
                    {t("Decision option preselected", {
                      ns: "QuoteItem",
                    })}
                  </Typography>
                  <DecisionOptionRadioOrCheckbox
                    projectId={projectId}
                    docId={docId}
                    item={item}
                    disabled={!canEdit}
                    expandedItemIds={expandedItemIds}
                  />
                </Stack>
                {canEdit && (
                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                    capability="expressions"
                    permissionByType={{
                      QUOTE: "quote_expressions_update",
                      REQUIREMENT: "requirement_expressions_update",
                      TEMPLATE: "template_expressions_update",
                      BUILDING: "building_expressions_update",
                    }}
                    document={document}
                    otherwise={
                      preselectDecisionOptionIfExpression?.expr ? (
                        <LabeledValue
                          label={t("Condition", { ns: "QuoteItem" })}
                          labelIcon={FunctionsIcon}
                        >
                          {preselectDecisionOptionIfExpression.expr}
                        </LabeledValue>
                      ) : undefined
                    }
                  >
                    <ModalOpenButton
                      Modal={AttributeExpressionModal}
                      modalProps={{
                        projectId,
                        docId: docId,
                        itemId: item.id,
                        fieldName: "preselectDecisionOptionIf",
                        expression: preselectDecisionOptionIfExpression?.expr,
                      }}
                    >
                      {preselectDecisionOptionIfExpression ? (
                        <LabeledValueWithButton
                          label={t("Condition", { ns: "QuoteItem" })}
                          labelIcon={FunctionsIcon}
                        >
                          {preselectDecisionOptionIfExpression.expr}
                        </LabeledValueWithButton>
                      ) : (
                        <AddConditionButton />
                      )}
                    </ModalOpenButton>
                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                )}
              </Stack>
            )}
          </>
        ) : (
          <>
            {canEdit && (
              <Stack
                sx={{
                  alignItems: "flex-start",
                }}
              >
                <Stack
                  direction={"row"}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    alignSelf: "stretch",
                  }}
                >
                  <FormLabel sx={{ color: "text.primary" }}>
                    {t("Item is eliminated", {
                      ns: "QuoteItem",
                    })}
                  </FormLabel>
                  <Checkbox
                    id={"decisionElimination"}
                    disabled={!canEdit || loading}
                    checked={item.isItemEliminated}
                    onChange={handleItemEliminationToggle}
                    size="small"
                  />
                </Stack>
                <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                  capability="expressions"
                  permissionByType={{
                    QUOTE: "quote_expressions_update",
                    REQUIREMENT: "requirement_expressions_update",
                    TEMPLATE: "template_expressions_update",
                    BUILDING: "building_expressions_update",
                  }}
                  document={document}
                  otherwise={
                    eliminateItemIfExpression?.expr ? (
                      <LabeledValue
                        label={t("Condition", { ns: "QuoteItem" })}
                        labelIcon={FunctionsIcon}
                      >
                        {eliminateItemIfExpression.expr}
                      </LabeledValue>
                    ) : undefined
                  }
                >
                  <ModalOpenButton
                    Modal={AttributeExpressionModal}
                    modalProps={{
                      projectId,
                      docId,
                      itemId: item.id,
                      fieldName: "eliminateItemIf",
                      expression: eliminateItemIfExpression?.expr,
                    }}
                  >
                    {eliminateItemIfExpression ? (
                      <LabeledValueWithButton
                        label={t("Condition", { ns: "QuoteItem" })}
                        labelIcon={FunctionsIcon}
                      >
                        {eliminateItemIfExpression.expr}
                      </LabeledValueWithButton>
                    ) : (
                      <AddConditionButton />
                    )}
                  </ModalOpenButton>
                </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
              </Stack>
            )}
            {!item.isItemEliminated && (
              <>
                <Stack
                  sx={{
                    alignItems: "flex-start",
                  }}
                >
                  <Stack
                    direction={"row"}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignSelf: "stretch",
                    }}
                  >
                    <FormLabel sx={{ color: "text.primary" }}>
                      {t("Item is optional", {
                        ns: "QuoteItem",
                      })}
                    </FormLabel>
                    <Switch
                      id={"contingency"}
                      checked={item.decisionIsContingentItem}
                      onChange={onChangeDecisionIs}
                      disabled={!canEdit || loading || item.isItemEliminated}
                    />
                  </Stack>
                  {canEdit && (
                    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                      capability="expressions"
                      permissionByType={{
                        QUOTE: "quote_expressions_update",
                        REQUIREMENT: "requirement_expressions_update",
                        TEMPLATE: "template_expressions_update",
                        BUILDING: "building_expressions_update",
                      }}
                      document={document}
                      otherwise={
                        decisionIsContingentItemExpression?.expr ? (
                          <LabeledValue
                            label={t("Condition", { ns: "QuoteItem" })}
                            labelIcon={FunctionsIcon}
                          >
                            {decisionIsContingentItemExpression.expr}
                          </LabeledValue>
                        ) : undefined
                      }
                    >
                      <ModalOpenButton
                        Modal={AttributeExpressionModal}
                        modalProps={{
                          projectId,
                          docId,
                          itemId: item.id,
                          fieldName: "decisionIsContingentItem",
                          expression: decisionIsContingentItemExpression?.expr,
                        }}
                      >
                        {decisionIsContingentItemExpression ? (
                          <LabeledValueWithButton
                            label={t("Condition", { ns: "QuoteItem" })}
                            labelIcon={FunctionsIcon}
                          >
                            {decisionIsContingentItemExpression.expr}
                          </LabeledValueWithButton>
                        ) : (
                          <AddConditionButton />
                        )}
                      </ModalOpenButton>
                    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                  )}
                </Stack>
                {(item.decisionIsContingentItem === true ||
                  decisionIsContingentItemExpression !== undefined) && (
                  <Stack
                    sx={{
                      alignItems: "flex-start",
                    }}
                  >
                    <Stack
                      direction={"row"}
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        alignSelf: "stretch",
                      }}
                    >
                      <FormLabel sx={{ color: "text.primary" }}>
                        {t("Preselect option", {
                          ns: "QuoteItem",
                        })}
                      </FormLabel>
                      <Checkbox
                        id={"contingencyPreselection"}
                        disabled={
                          !canEdit ||
                          loading ||
                          item.decisionIsContingentItem !== true
                        }
                        checked={
                          item.decisionContingentItemPreselection === true
                        }
                        indeterminate={
                          item.decisionContingentItemPreselection === null
                        }
                        onChange={onChangeDecisionPreselection}
                        size="small"
                      />
                    </Stack>
                    {canEdit && (
                      <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                        capability="expressions"
                        permissionByType={{
                          QUOTE: "quote_expressions_update",
                          REQUIREMENT: "requirement_expressions_update",
                          TEMPLATE: "template_expressions_update",
                          BUILDING: "building_expressions_update",
                        }}
                        document={document}
                        otherwise={
                          decisionContingentItemPreselectionExpression?.expr ? (
                            <LabeledValue
                              label={t("Condition", { ns: "QuoteItem" })}
                              labelIcon={FunctionsIcon}
                            >
                              {
                                decisionContingentItemPreselectionExpression.expr
                              }
                            </LabeledValue>
                          ) : undefined
                        }
                      >
                        <ModalOpenButton
                          Modal={AttributeExpressionModal}
                          modalProps={{
                            projectId,
                            docId,
                            itemId: item.id,
                            fieldName: "decisionContingentItemPreselection",
                            expression:
                              decisionContingentItemPreselectionExpression?.expr,
                          }}
                        >
                          {decisionContingentItemPreselectionExpression ? (
                            <LabeledValueWithButton
                              label={t("Condition", { ns: "QuoteItem" })}
                              labelIcon={FunctionsIcon}
                            >
                              {
                                decisionContingentItemPreselectionExpression.expr
                              }
                            </LabeledValueWithButton>
                          ) : (
                            <AddConditionButton />
                          )}
                        </ModalOpenButton>
                      </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                    )}
                  </Stack>
                )}
                {(item.decisionIsContingentItem === true ||
                  decisionIsContingentItemExpression !== undefined) && (
                  <ContingentQuestionControlForm
                    projectId={projectId}
                    docId={docId}
                    item={item}
                    canEdit={canEdit}
                    refetchWizardQuestions={refetchWizardQuestions}
                  />
                )}
              </>
            )}
          </>
        )}
      </Stack>
    </CardContainer>
  );
}

function AddConditionButton(props: ButtonProps) {
  const { t } = useTranslate(["QuoteItem"]);

  return (
    <Button
      startIcon={<AddIcon />}
      size={"small"}
      color={"secondary"}
      {...props}
    >
      {t("Add condition", { ns: "QuoteItem" })}
    </Button>
  );
}

function ContingentQuestionControlForm({
  projectId,
  docId,
  item,
  canEdit = true,
  refetchWizardQuestions,
}: {
  projectId: string | null;
  docId: string;
  item: RulesBox_QuestionControlForm_ItemFragment;
  canEdit?: boolean;
  refetchWizardQuestions: () => Promise<unknown>;
}) {
  const client = useApolloClient();
  const [modifyWizardSettings] =
    useRulesBox_QuestionControlForm_ModifyWizardSettingsMutationWithWizardRefetch(
      {
        client,
        refetchWizardQuestions,
      }
    );

  return (
    <Formik<QuestionControlSectionFormValues>
      initialValues={{
        questionControl: item.wizardSettings.contingentQuestionControl,
        // askWhen:
        //   item.wizardSettings.contingentQuestionControl.at(0)?.askWhen ?? [],
        // askWhom:
        //   item.wizardSettings.contingentQuestionControl.at(0)?.askWhom ?? [],
        prompt: item.wizardSettings.promptContingent,
      }}
      onSubmit={async values => {
        await modifyWizardSettings({
          variables: {
            input: {
              projectId,
              docId,
              itemId: item.id,
              values: {
                contingentQuestionControl: values.questionControl,
                // contingentQuestionControl: [
                //   {
                //     askWhen: values.askWhen,
                //     askWhom: values.askWhom,
                //   },
                // ],
                promptContingent: values.prompt,
              },
            },
          },
        });
      }}
    >
      <Form>
        <QuestionControlSection
          readOnly={!canEdit}
          isInitiallyExpanded
          disabled={false}
        />
        <AutoSave />
      </Form>
    </Formik>
  );
}
